import { blockPalette, blockTheme, ContentBlockStyles } from "@gaiaProfessionals/Widgets/HCP/ContentBlocks/HCPContentBlocks.styles";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import React, { FC } from "react";
import styled, { css } from "styled-components";

// <============== Fields Data ==============>
const enum fields {
    productImage = "productImage",
    featuredHeader = "featuredHeader",
    featuredSubHeader = "featuredSubHeader",
    btnLabel = "btnLabel",
    btnLink = "btnLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.productImage]: string;
        [fields.featuredHeader]: string;
        [fields.featuredSubHeader]: string;
        [fields.btnLabel]: string;
        [fields.btnLink]: LinkFieldValue;
    };
}

// <============== Categories Block Styled Component ==============>
interface FeaturedProps {
    fields: {

    };
}

const FeaturedItem = styled.div<FeaturedProps>`
    margin: 1.5rem 0;
    position: relative;
    width: 100%;
    // Media Queries
    /* Small Devices, Tablets */
    @media screen and (min-width: 768px) {
        padding: 0 8%;
    }
    /* Large Devices, Wide Screens */
    @media screen and (min-width: 1400px) {
        padding: 0 5%;
    }

    /* =============== Featured Button =============== */
    .featuredBtn {
        margin-top: auto;
        padding: 0 40% 0 5%;
        width: 100%;
        min-height: 108px;
        display: flex;
        align-items: center;
        // Media Queries
        /* Small Devices, Tablets */
        @media screen and (min-width: 768px) {
            padding: 0 5%;
        }
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            padding: 0 40% 0 5%;
        }
        // Sub Elements
        .btn {
            margin: 0;
        }
    }

    /* =============== Featured Item =============== */
    .featuredItem {
        margin-bottom: 1.25rem;
        display: grid;
        justify-content: center;
        position: relative;
    }

    .featuredItem__img {
        opacity: 1;
        transition: opacity 0.3s ease-in-out;
    }
    a:focus .featuredItem__img,
    a:hover .featuredItem__img {
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            opacity: .5;
            transition: opacity 0.3s ease-in-out;
        }
    }

    .featuredItem__btn {
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            display: none;
        }
    }
    .featuredItem__btn.featuredItem__btn--float {
        display: none;
        position: absolute;
        justify-self: center;
        align-self: center;
        opacity: 0;
        color: ${blockPalette.green};
        background-color: ${blockPalette.white};
        border-color: ${blockPalette.green};
        transition: opacity 0.3s ease-in-out;
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            display: block;
        }
    }
    a:focus .featuredItem__btn.featuredItem__btn--float,
    a:hover .featuredItem__btn.featuredItem__btn--float {
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            opacity: 1;
            background-color: ${blockPalette.white};
            border-color: ${blockPalette.green};
            transition: opacity 0.3s ease-in-out;
        }
    }
    // Based off the theme set in the Featured Block Widget
    .light & .featuredItem__btn {
        border-color: ${blockPalette.darkGreen};
        color: ${blockPalette.darkGreen};
        // Hover & Focus
        &:focus,
        &:hover {
            background-color: ${blockPalette.darkGreen};
            color: ${blockPalette.white};
        }
        // Sub Elements
        & > span[class^="TypographyStyle"] {
            color: ${blockPalette.darkGreen};
        }
        &:focus > span[class^="TypographyStyle"],
        &:hover > span[class^="TypographyStyle"] {
            color: ${blockPalette.white};
        }
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            &:focus,
            &:hover {
                background-color: ${blockPalette.white};
                color: ${blockPalette.darkGreen};
            }
        }
    }

    /* =============== Text Block =============== */
    .textBlock__head--item {
        font-size: 1.875rem;
    }

    .textBlock__subHead--item {
        font-size: 1rem;
    }

`;

// <============== Component ==============>
const HCPFeaturedBlockItem: FC<OwnProps> = ({ fields }) => {

    const { url } = useGetLink(fields.btnLink);

    return (
        <FeaturedItem fields={fields}>
            <Link className="featuredLink" href={url}>
                <div className="featuredItem">
                    {fields.productImage && <img className="featuredItem__img" src={fields.productImage} alt="product" aria-hidden="true" />}
                    {url && (
                        <div className="btn btn--ghost-primary featuredItem__btn featuredItem__btn--float" aria-hidden="true">
                            {fields.btnLabel}
                        </div>
                    )}
                </div>
                <div className="textBlock">
                    {fields.featuredHeader && <h2 className="textBlock__head textBlock__head--item">
                        {fields.featuredHeader.length > 50
                            ? `${fields.featuredHeader.substring(0, 50)}...`
                            : fields.featuredHeader
                        }
                    </h2>}
                </div>
            </Link>
            {fields.featuredSubHeader && <p className="textBlock__subHead textBlock__subHead--item">{fields.featuredSubHeader}</p>}
            {url && (
                <Link className="btn btn--ghost-primary featuredItem__btn" href={url}>
                    {fields.btnLabel}
                </Link>
            )}
        </FeaturedItem>
    );
};

// <============== Widget Fields ==============>
const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const HCPFeaturedItem: WidgetModule = {
    component: HCPFeaturedBlockItem,
    definition: {
        group: "Common",
        icon: "Banner",
        displayName: "HCP Featured Item",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.productImage,
                displayName: "Item Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.featuredHeader,
                editorTemplate: "TextField",
                displayName: "Header Text",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.featuredSubHeader,
                editorTemplate: "TextField",
                displayName: "Sub Header Text",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.btnLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "View Details",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.btnLink,
                displayName: "Content Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: contentTab,
            },
        ],
    },
};

export default HCPFeaturedItem;